import { useEffect, useState } from "react";
import axios from "axios";
import { ChevronRight, Download, Link2Off, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API } from "@/context/AuthContext";
import { inr, inrShort } from "@/pages/Dashboard";

const Tile = ({ label, value, tone, testId }) => (
  <div className="rounded-md border border-slate-200 bg-white px-3 py-2" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-wider text-slate-400">{label}</p>
    <p className={`text-base font-bold ${tone || "text-slate-900"}`}>{value}</p>
  </div>
);

const SupplierBlock = ({ s, open, onToggle, testPrefix }) => (
  <>
    <tr className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={onToggle} data-testid={`${testPrefix}-unlinked-supplier-${s.vendor_code}`}>
      <td className="py-2 pr-3"><ChevronRight className={`inline h-3.5 w-3.5 text-slate-400 transition-transform ${open ? "rotate-90" : ""}`} /> <span className="font-medium text-slate-800">{s.vendor_name}</span> <span className="text-slate-400 font-mono">{s.vendor_code}</span></td>
      <td className="py-2 pr-3 text-right tabular-nums">{s.count}</td>
      <td className="py-2 pr-3 text-right tabular-nums">{s.advance ? inr(s.advance) : "—"}</td>
      <td className="py-2 pr-3 text-right tabular-nums">{s.on_account ? inr(s.on_account) : "—"}</td>
      <td className="py-2 pr-3 text-right tabular-nums font-semibold">{inr(s.unlinked)}</td>
      <td className="py-2 text-right tabular-nums text-slate-600">{s.open_bills ? `${s.open_bills} · ${inr(s.open_bill_amount)}` : "none"}</td>
    </tr>
    {open && s.items.map((r) => (
      <tr key={`${r.doc}-${r.item}`} className="bg-slate-50/70 border-b border-slate-100 text-[11px]" data-testid={`${testPrefix}-unlinked-item-${r.doc}-${r.item}`}>
        <td className="py-1.5 pl-8 pr-3" colSpan={2}><span className="font-mono">{r.doc}</span> · {r.doc_type}{r.special_gl ? ` · SGL ${r.special_gl}` : ""} · {r.doc_date} · {r.age_days}d
          {r.narration && <span className="block text-slate-500 truncate max-w-[420px]" title={r.narration}>“{r.narration}”</span>}</td>
        <td className="py-1.5 pr-3 text-slate-600" colSpan={2}>{r.kind}{r.adjusted ? ` · ${inr(r.adjusted)} already adjusted` : ""}</td>
        <td className="py-1.5 pr-3 text-right tabular-nums font-semibold">{inr(r.unlinked)}</td>
        <td className="py-1.5 text-right text-slate-500">{r.hint}</td>
      </tr>
    ))}
  </>
);

export const UnlinkedPayments = ({ asOn, testPrefix }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dl, setDl] = useState(false);
  const [q, setQ] = useState("");
  const [exp, setExp] = useState(new Set());
  const params = asOn && asOn !== "today" ? { as_on: asOn } : {};

  useEffect(() => {
    if (!open) return;
    setBusy(true);
    axios.get(`${API}/reports/ap-ageing/unlinked-payments`, { params }).then((r) => setData(r.data))
      .catch(() => toast.error("Could not load unlinked payments")).finally(() => setBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, asOn]);

  const download = async () => {
    setDl(true);
    try {
      const r = await axios.get(`${API}/reports/ap-ageing/unlinked-payments`, { params: { ...params, format: "xlsx" }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `ap-unlinked-payments-${data?.as_on || "today"}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); } finally { setDl(false); }
  };
  const toggle = (c) => setExp((s) => { const n = new Set(s); n.has(c) ? n.delete(c) : n.add(c); return n; });
  const shown = (data?.suppliers || []).filter((s) => !q || `${s.vendor_name} ${s.vendor_code}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid={`${testPrefix}-unlinked-open`} className="h-9 border-slate-300 text-slate-700">
        <Link2Off className="h-4 w-4 mr-1.5" />Unlinked payments
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-6xl" data-testid={`${testPrefix}-unlinked-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-3 pr-6">
              <span>Payments &amp; advances not linked to a bill{data ? ` · as on ${data.as_on}` : ""}</span>
              <Button size="sm" variant="outline" onClick={download} disabled={dl || !data} data-testid={`${testPrefix}-unlinked-download`} className="border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100">
                {dl ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1" />}Excel
              </Button>
            </DialogTitle>
          </DialogHeader>
          {busy || !data ? (
            <div className="py-16 text-center text-sm text-slate-500"><Loader2 className="inline h-4 w-4 mr-2 animate-spin" />Matching payments to bills (reads SAP references)…</div>
          ) : (
            <>
              <p className="text-xs text-slate-500">Open debits after knock-off by SAP invoice reference and by bill no. in the narration. Where the supplier still has open bills, these can be cleared against them in SAP.{data.note ? ` ${data.note}.` : ""}</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <Tile label={`Not linked · ${data.count} items`} value={inrShort(data.total)} testId={`${testPrefix}-unlinked-total`} />
                <Tile label="Advances (SGL A)" value={inrShort(data.advance_total)} testId={`${testPrefix}-unlinked-advance`} />
                <Tile label="Payments / debits on account" value={inrShort(data.on_account_total)} testId={`${testPrefix}-unlinked-onaccount`} />
                <Tile label="Clearable against open bills" value={inrShort(data.clearable)} tone="text-emerald-700" testId={`${testPrefix}-unlinked-clearable`} />
              </div>
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter supplier" className="h-8 w-64 text-sm" data-testid={`${testPrefix}-unlinked-search`} />
              <div className="max-h-[52vh] overflow-auto">
                <table className="w-full text-xs" data-testid={`${testPrefix}-unlinked-table`}>
                  <thead className="sticky top-0 bg-white">
                    <tr className="text-slate-500 uppercase tracking-wider text-[10px] border-b border-slate-200">
                      <th className="text-left py-2 pr-3">Supplier</th><th className="text-right py-2 pr-3">Items</th><th className="text-right py-2 pr-3">Advance</th>
                      <th className="text-right py-2 pr-3">On account</th><th className="text-right py-2 pr-3">Not linked</th><th className="text-right py-2">Open bills</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((s) => <SupplierBlock key={s.vendor_code} s={s} open={exp.has(s.vendor_code)} onToggle={() => toggle(s.vendor_code)} testPrefix={testPrefix} />)}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
